// Phase RV-5 — Upcoming Picks Board helpers.
//
// Source: ROLLOVER_Intelligence_Enhancement_v1.0 §11.1 (Upcoming Picks Board),
// Table 26 (Board Layout), Table 27 (Free vs Paid gate), Table 28 (Board Timing).
//
// Pure / I/O-free. All time arithmetic done in WAT (Africa/Lagos = UTC+1, no DST).
//
// Publication times (Table 28):
//   midweek    → Monday 20:15 WAT  (= Monday 19:15 UTC)
//   weekend    → Friday 12:15 WAT  (= Friday 11:15 UTC)
//   supplement → Saturday 10:15 WAT (= Saturday 09:15 UTC)
//
// Window lifecycle: visible from publication until the first kickoff of the
// window (which transitions the board to LIVE).

import type { RolloverTier } from "./tierConfig.ts";

export type WindowLabel = "midweek" | "weekend" | "supplement";

/** UTC hour offsets from WAT. WAT = UTC+1 → subtract 1 to get UTC. */
const WAT_OFFSET_HOURS = 1;

/** WAT publication hour:minute (Table 28). */
const PUB_TIMES: Record<WindowLabel, { day: number; hour: number; minute: number }> = {
  // day: 0=Sun..6=Sat
  midweek: { day: 1, hour: 20, minute: 15 }, // Mon 20:15 WAT
  weekend: { day: 5, hour: 12, minute: 15 }, // Fri 12:15 WAT
  supplement: { day: 6, hour: 10, minute: 15 }, // Sat 10:15 WAT
};

/** Build a UTC Date for a given WAT day/hour/minute relative to a reference week. */
function watToUtcThisWeek(
  ref: Date,
  watDay: number,
  watHour: number,
  watMinute: number,
): Date {
  // Find the WAT calendar day-of-week for `ref`.
  const refWatMs = ref.getTime() + WAT_OFFSET_HOURS * 3_600_000;
  const refWat = new Date(refWatMs);
  const refWatDay = refWat.getUTCDay();
  const dayDiff = watDay - refWatDay;
  // Construct target in WAT (as if UTC).
  const watTarget = new Date(refWatMs);
  watTarget.setUTCDate(watTarget.getUTCDate() + dayDiff);
  watTarget.setUTCHours(watHour, watMinute, 0, 0);
  // Convert back to UTC by subtracting offset.
  return new Date(watTarget.getTime() - WAT_OFFSET_HOURS * 3_600_000);
}

/**
 * Returns the publication moment (Date) for the given window in the week
 * containing `now`. May be earlier OR later than `now`.
 */
export function currentWindowPublicationTime(
  windowLabel: WindowLabel,
  now: Date = new Date(),
): Date {
  const spec = PUB_TIMES[windowLabel];
  return watToUtcThisWeek(now, spec.day, spec.hour, spec.minute);
}

/**
 * Returns the NEXT publication moment for any window relative to `now`.
 * Used by the empty-state Countdown.
 */
export function nextPublicationTime(now: Date = new Date()): {
  windowLabel: WindowLabel;
  at: Date;
} {
  let best: { windowLabel: WindowLabel; at: Date } | null = null;
  for (const w of ["midweek", "weekend", "supplement"] as WindowLabel[]) {
    let at = currentWindowPublicationTime(w, now);
    if (at.getTime() <= now.getTime()) {
      // Roll forward 7 days.
      at = new Date(at.getTime() + 7 * 86_400_000);
    }
    if (!best || at.getTime() < best.at.getTime()) {
      best = { windowLabel: w, at };
    }
  }
  // Non-null: the loop always assigns at least once.
  return best as { windowLabel: WindowLabel; at: Date };
}

/**
 * Detect the currently-active board window (publication has happened, first
 * kickoff has not arrived OR is in progress). Returns null between windows.
 *
 * Active window rule:
 *   - midweek window is active from Mon 20:15 WAT until Fri 12:15 WAT.
 *   - weekend window is active from Fri 12:15 WAT until Sat 10:15 WAT.
 *   - supplement window is active from Sat 10:15 WAT until Mon 20:15 WAT next week.
 *
 * The first-kickoff transition is handled at the data layer (LIVE state),
 * not here — the window label remains active until the next publication.
 */
export function detectCurrentWindow(now: Date = new Date()): WindowLabel | null {
  const midPub = currentWindowPublicationTime("midweek", now);
  const weekPub = currentWindowPublicationTime("weekend", now);
  const suppPub = currentWindowPublicationTime("supplement", now);

  // Walk publication boundaries within the week containing `now`.
  // Order: Mon midweek → Fri weekend → Sat supplement → next Mon midweek.
  const nextMidPub = new Date(midPub.getTime() + 7 * 86_400_000);

  const t = now.getTime();
  // Anchor: if now is before this week's midweek pub, we are in last week's
  // supplement OR earlier. Walk back to last Saturday.
  if (t < midPub.getTime()) {
    const prevSuppPub = new Date(suppPub.getTime() - 7 * 86_400_000);
    if (t >= prevSuppPub.getTime()) return "supplement";
    // Pre-Sat: in last week's weekend window.
    const prevWeekPub = new Date(weekPub.getTime() - 7 * 86_400_000);
    if (t >= prevWeekPub.getTime()) return "weekend";
    // Pre-Fri: in last week's midweek window.
    const prevMidPub = new Date(midPub.getTime() - 7 * 86_400_000);
    if (t >= prevMidPub.getTime()) return "midweek";
    return null;
  }
  // t >= midPub
  if (t < weekPub.getTime()) return "midweek";
  if (t < suppPub.getTime()) return "weekend";
  if (t < nextMidPub.getTime()) return "supplement";
  return null;
}

/** Compact summary for a single board candidate (subset of TrackerCandidate). */
export interface BoardCandidate {
  candidate_id: string;
  fixture_id: string | null;
  match_name: string;
  league: string | null;
  kickoff_at: string | null;
  market: string | null;
  pick: string | null;
  odds: number | null;
  confidence_score: number | null;
  value_edge_pct: number | null;
  clv_edge_detected: boolean;
  pool_tag: "selected" | "evaluated" | "assessed";
  discard_reason: string | null;
  agent_reasoning: string | null;
  filter_stage_failed: string | null;
  tier: RolloverTier;
  window_label: WindowLabel;
}

/** Earliest kickoff timestamp across the supplied candidates (null if none). */
export function firstKickoffAt(candidates: BoardCandidate[]): Date | null {
  let best: number | null = null;
  for (const c of candidates) {
    if (!c.kickoff_at) continue;
    const t = new Date(c.kickoff_at).getTime();
    if (!Number.isFinite(t)) continue;
    if (best === null || t < best) best = t;
  }
  return best === null ? null : new Date(best);
}

export type TierLabel = RolloverTier;

/** Group by tier — returns a complete record with empty arrays for absent tiers. */
export function groupByTier(
  candidates: BoardCandidate[],
): Record<TierLabel, BoardCandidate[]> {
  const out: Record<TierLabel, BoardCandidate[]> = {
    "2x": [],
    "3x": [],
    "4x": [],
    "5x": [],
    "6x": [],
  };
  for (const c of candidates) {
    out[c.tier].push(c);
  }
  return out;
}

// ─── Gated view (Table 27) ────────────────────────────────────────────────

export type GatedConfidence =
  | { kind: "dots"; dots: number }
  | { kind: "score"; score: number };

export type GatedClv =
  | { kind: "badge"; badge: "EDGE_DETECTED" }
  | { kind: "full"; edge_pct: number; model_prob: number; market_prob: number }
  | null;

export type GatedReasoning =
  | { kind: "teaser"; teaser: string; locked: true }
  | { kind: "full"; full: string; locked: false };

export interface GatedFilterStage {
  passed: string[];
  scores_locked: boolean;
  scores?: Record<string, number>;
}

export type GatedRejection =
  | { kind: "label"; label: string; detail_locked: true }
  | { kind: "full"; full: string; detail_locked: false }
  | null;

export interface GatedView {
  confidence: GatedConfidence;
  clv: GatedClv;
  reasoning: GatedReasoning;
  filter_stage: GatedFilterStage;
  rejection_reason: GatedRejection;
}

/** Convert a 0–100 confidence to a 0–5 dot count (free view). */
export function confidenceToDots(score: number | null): number {
  if (score === null || !Number.isFinite(score)) return 0;
  if (score >= 85) return 5;
  if (score >= 70) return 4;
  if (score >= 55) return 3;
  if (score >= 40) return 2;
  if (score > 0) return 1;
  return 0;
}

/** First N words helper for the reasoning teaser. */
function firstWords(s: string, n: number): string {
  return s.split(/\s+/).filter(Boolean).slice(0, n).join(" ");
}

/**
 * Implement Table 27 free vs paid gate.
 *
 * IMPORTANT: free path never includes locked field VALUES (no scores, no
 * full reasoning, no full discard reason). Vitest asserts this strictly.
 */
export function formatCandidateGate(
  candidate: BoardCandidate,
  tier: "free" | "pro",
): GatedView {
  const isPro = tier === "pro";

  // Confidence.
  const confidence: GatedConfidence = isPro
    ? { kind: "score", score: candidate.confidence_score ?? 0 }
    : { kind: "dots", dots: confidenceToDots(candidate.confidence_score) };

  // CLV.
  let clv: GatedClv = null;
  if (candidate.clv_edge_detected) {
    if (isPro && candidate.value_edge_pct !== null) {
      const edge = candidate.value_edge_pct;
      // Model and market probabilities back-derived from edge_pct as a
      // descriptive pair. Where the raw probabilities aren't on the candidate
      // we fall back to neutral 50/50 so the field can still render.
      const marketProb = 0.5;
      const modelProb = Math.min(0.99, Math.max(0.01, marketProb + edge / 100));
      clv = {
        kind: "full",
        edge_pct: edge,
        model_prob: modelProb,
        market_prob: marketProb,
      };
    } else if (!isPro) {
      clv = { kind: "badge", badge: "EDGE_DETECTED" };
    }
  }

  // Reasoning.
  const reasoning: GatedReasoning = isPro
    ? {
        kind: "full",
        full: candidate.agent_reasoning ?? "",
        locked: false,
      }
    : {
        kind: "teaser",
        teaser: candidate.agent_reasoning
          ? firstWords(candidate.agent_reasoning, 8)
          : "",
        locked: true,
      };

  // Filter stage — both tiers see WHICH stages passed; only Pro sees scores.
  const passed: string[] = [];
  if (candidate.pool_tag === "selected" || candidate.pool_tag === "evaluated") {
    passed.push("stage_1_scan", "stage_2_form_value", "stage_3_odds");
    if (candidate.tier === "4x" || candidate.tier === "5x" || candidate.tier === "6x") {
      passed.push("stage_5_adversarial");
    }
  } else if (candidate.filter_stage_failed) {
    // Failed at a stage — list the stages before the failed one.
    const order = [
      "stage_1_scan",
      "stage_2_form_value",
      "stage_3_odds",
      "stage_5_adversarial",
    ];
    const idx = order.indexOf(candidate.filter_stage_failed);
    if (idx > 0) passed.push(...order.slice(0, idx));
  }
  const filter_stage: GatedFilterStage = isPro
    ? {
        passed,
        scores_locked: false,
        scores: {
          confidence: candidate.confidence_score ?? 0,
          value_edge_pct: candidate.value_edge_pct ?? 0,
        },
      }
    : { passed, scores_locked: true };

  // Rejection reason.
  let rejection_reason: GatedRejection = null;
  if (candidate.discard_reason) {
    rejection_reason = isPro
      ? { kind: "full", full: candidate.discard_reason, detail_locked: false }
      : {
          kind: "label",
          label: candidate.filter_stage_failed
            ? `Did not qualify · ${candidate.filter_stage_failed}`
            : "Did not qualify",
          detail_locked: true,
        };
  }

  return {
    confidence,
    clv,
    reasoning,
    filter_stage,
    rejection_reason,
  };
}

/** Friendly WAT date label for the board header. */
export function formatWindowDate(
  windowLabel: WindowLabel,
  publicationTime: Date,
): string {
  return publicationTime.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "Africa/Lagos",
  });
}
